import { z } from "zod";
import { reportComponentThemeSchema } from "@/components/ui/report/reportPresentation";
import { parseGoogleFontsStylesheetUrl } from "@/lib/template-studio/brandAssets";
import { chartHexColorSchema } from "@/lib/utils/chartUtils";
import {
  TEMPLATE_LAYOUT_DOCUMENT_SCHEMA_VERSION,
  type TemplateLayoutDocument,
  type TemplateLayoutHexColor,
  type TemplateLayoutValidationError,
  type TemplateSlotContentType,
} from "@/types/template-layout/templateLayout.types";
import {
  getDefaultTemplateSlotInputMode,
  TEMPLATE_SLOT_CONTENT_TYPES,
  TEMPLATE_SLOT_INPUT_MODES,
} from "@/types/template-layout/templateSlotContent.types";

const MAX_CATEGORICAL_PALETTE_COLORS = 8;

const templateSlotContentTypeSchema = z.custom<TemplateSlotContentType>(
  (value) =>
    typeof value === "string" &&
    (TEMPLATE_SLOT_CONTENT_TYPES as readonly string[]).includes(value),
  {
    message: `contentType must be one of: ${TEMPLATE_SLOT_CONTENT_TYPES.join(", ")}`,
  },
);

/** Expands `#RGB` to lowercase `#rrggbb`; leaves six-digit hex lowercase. */
export const normalizeTemplateLayoutHexColor = (hex: string): string => {
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    const [, r, g, b] = hex;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  return hex.toLowerCase();
};

const templateLayoutHexColorSchema = chartHexColorSchema.transform(
  (hex) => normalizeTemplateLayoutHexColor(hex) as TemplateLayoutHexColor,
);

const chartThemeSchema = z
  .object({
    version: z.literal(1),
    strategy: z.enum(["monochromatic", "categorical", "entity"]),
    baseColor: templateLayoutHexColorSchema,
    categoricalPalette: z.array(templateLayoutHexColorSchema).optional(),
  })
  .superRefine((chartTheme, ctx) => {
    const palette = chartTheme.categoricalPalette;
    if (palette === undefined) return;

    if (palette.length > MAX_CATEGORICAL_PALETTE_COLORS) {
      ctx.addIssue({
        code: "custom",
        message: `categoricalPalette must contain at most ${MAX_CATEGORICAL_PALETTE_COLORS} colours`,
        path: ["categoricalPalette"],
      });
    }
  });

const templateSlotConstraintsSchema = z
  .object({
    maxItems: z.number().int().positive().optional(),
    maxCharacters: z.number().int().positive().optional(),
    maxSeries: z.number().int().positive().optional(),
  })
  .strict()
  .optional();

const templateSlotSchema = z
  .object({
    id: z.string().trim().min(1, "slot id is required"),
    label: z.string().trim().min(1, "slot label is required"),
    contentType: templateSlotContentTypeSchema,
    inputMode: z.enum(TEMPLATE_SLOT_INPUT_MODES).optional(),
    constraints: templateSlotConstraintsSchema,
  })
  .transform((slot) => ({
    ...slot,
    inputMode:
      slot.inputMode ?? getDefaultTemplateSlotInputMode(slot.contentType),
  }));

const templateLayoutSpecElementSchema = z
  .object({
    type: z.string().trim().min(1, "element type is required"),
    props: z.record(z.string(), z.unknown()).optional(),
    children: z.array(z.string()).optional(),
    slots: z.record(z.string(), z.array(z.string())).optional(),
    visible: z.unknown().optional(),
    on: z.record(z.string(), z.unknown()).optional(),
    repeat: z
      .object({
        statePath: z.string().optional(),
        key: z.string().optional(),
      })
      .optional(),
    watch: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

const hasElementCycle = (
  elements: TemplateLayoutDocument["spec"]["elements"],
): boolean => {
  const incomingEdges = new Map(
    Object.keys(elements).map((elementId) => [elementId, 0]),
  );

  for (const element of Object.values(elements)) {
    for (const childId of element.children ?? []) {
      const incomingEdgeCount = incomingEdges.get(childId);
      if (incomingEdgeCount !== undefined) {
        incomingEdges.set(childId, incomingEdgeCount + 1);
      }
    }
  }

  const queue = [...incomingEdges]
    .filter(([, incomingEdgeCount]) => incomingEdgeCount === 0)
    .map(([elementId]) => elementId);
  let visitedCount = 0;

  for (const elementId of queue) {
    visitedCount += 1;

    for (const childId of elements[elementId]?.children ?? []) {
      const incomingEdgeCount = incomingEdges.get(childId);
      if (incomingEdgeCount === undefined) continue;

      const nextIncomingEdgeCount = incomingEdgeCount - 1;
      incomingEdges.set(childId, nextIncomingEdgeCount);
      if (nextIncomingEdgeCount === 0) queue.push(childId);
    }
  }

  return visitedCount !== incomingEdges.size;
};

const templateLayoutSpecSchema = z
  .object({
    root: z.string().trim().min(1, "spec root is required"),
    elements: z.record(z.string(), templateLayoutSpecElementSchema),
    state: z.record(z.string(), z.unknown()).optional(),
  })
  .superRefine((spec, ctx) => {
    if (!Object.hasOwn(spec.elements, spec.root)) {
      ctx.addIssue({
        code: "custom",
        message: `root "${spec.root}" is not defined in elements`,
        path: ["root"],
      });
    }

    for (const [elementId, element] of Object.entries(spec.elements)) {
      for (const [childIndex, childId] of (element.children ?? []).entries()) {
        if (!Object.hasOwn(spec.elements, childId)) {
          ctx.addIssue({
            code: "custom",
            message: `child "${childId}" is not defined in elements`,
            path: ["elements", elementId, "children", childIndex],
          });
        }
      }
    }

    if (hasElementCycle(spec.elements)) {
      ctx.addIssue({
        code: "custom",
        message: "element children must not contain cycles",
        path: ["elements"],
      });
    }
  });

const isTemporaryAssetReference = (value: string): boolean =>
  value.startsWith("blob:") ||
  value.startsWith("data:") ||
  /^https?:\/\//i.test(value);

const templateLayoutThemeSchema = z
  .object({
    ...reportComponentThemeSchema.shape,
    primaryColor: templateLayoutHexColorSchema,
    fontFamily: z.string().trim().min(1).optional(),
    fontStylesheetUrl: z.string().trim().url().optional(),
    logoAssetId: z.string().trim().min(1).optional(),
    chart: chartThemeSchema,
  })
  .superRefine((theme, ctx) => {
    if (
      theme.fontStylesheetUrl !== undefined &&
      parseGoogleFontsStylesheetUrl(theme.fontStylesheetUrl) === null
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "fontStylesheetUrl must be a valid https://fonts.googleapis.com stylesheet URL",
        path: ["fontStylesheetUrl"],
      });
    }

    if (
      theme.logoAssetId !== undefined &&
      isTemporaryAssetReference(theme.logoAssetId)
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "logoAssetId must be a permanent asset id, not a browser or data URL",
        path: ["logoAssetId"],
      });
    }
  });

const templateLayoutDocumentSchema: z.ZodType<TemplateLayoutDocument> = z
  .object({
    schemaVersion: z.literal(TEMPLATE_LAYOUT_DOCUMENT_SCHEMA_VERSION),
    catalogVersion: z.string().trim().min(1, "catalogVersion is required"),
    spec: templateLayoutSpecSchema,
    theme: templateLayoutThemeSchema,
    slots: z.array(templateSlotSchema),
  })
  .superRefine((document, ctx) => {
    const seenSlotIds = new Set<string>();

    for (const [index, slot] of document.slots.entries()) {
      if (seenSlotIds.has(slot.id)) {
        ctx.addIssue({
          code: "custom",
          message: `duplicate slot id "${slot.id}"`,
          path: ["slots", index, "id"],
        });
        continue;
      }

      seenSlotIds.add(slot.id);
    }
  });

const formatValidationPath = (path: PropertyKey[]): string | undefined => {
  if (path.length === 0) return undefined;
  return `/${path.map((segment) => String(segment)).join("/")}`;
};

const zodIssuesToValidationErrors = (
  issues: z.core.$ZodIssue[],
): TemplateLayoutValidationError[] =>
  issues.map((issue) => ({
    path: formatValidationPath(issue.path),
    message: issue.message,
  }));

export type ValidateTemplateLayoutResult =
  | { success: true; data: TemplateLayoutDocument }
  | { success: false; errors: TemplateLayoutValidationError[] };

/**
 * Parses and validates an unknown value as a {@link TemplateLayoutDocument}.
 * Colours are normalized to lowercase six-digit hex at the API boundary.
 */
export const validateTemplateLayout = (
  input: unknown,
): ValidateTemplateLayoutResult => {
  const result = templateLayoutDocumentSchema.safeParse(input);

  if (!result.success) {
    return {
      success: false,
      errors: zodIssuesToValidationErrors(result.error.issues),
    };
  }

  return { success: true, data: result.data };
};
